import React from 'react';
import { motion } from 'framer-motion';
import GlassPill from '@/components/ui/GlassPill';
import { PartyPopper, ArrowRight, Sparkles } from 'lucide-react';

interface LoadingSuccessProps {
  successMessage: string;
  actionButtonText: string;
  onActionClick: () => void;
  onClose: () => void;
}

const LoadingSuccess: React.FC<LoadingSuccessProps> = ({ successMessage, actionButtonText, onActionClick, onClose }) => {
  return (
    <div className="text-center space-y-8 py-4">
      {/* Success Icon with Animation */}
      <motion.div 
        className="flex justify-center"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ 
          type: "spring", 
          stiffness: 300, 
          damping: 20,
          delay: 0.2 
        }}
      >
        <div className="relative">
          <GlassPill 
            type="Process Completed!" 
            variant="green" 
            icon={<PartyPopper />} 
            className="text-lg px-6 py-3 shadow-lg"
          />
          {/* Sparkle effects */}
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Sparkles className="h-4 w-4 text-yellow-400" />
          </motion.div>
          <motion.div
            className="absolute -bottom-1 -left-2"
            animate={{ 
              rotate: [360, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          >
            <Sparkles className="h-3 w-3 text-blue-400" />
          </motion.div>
        </div>
      </motion.div>

      {/* Success Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="space-y-2"
      >
        <p className="text-lg font-medium text-foreground">{successMessage}</p>
        <p className="text-sm text-muted-foreground">
          Your creation is ready to explore!
        </p>
      </motion.div>

      {/* Separated Button Layout */}
      <motion.div 
        className="flex justify-between items-center gap-4 pt-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        {/* Close Button - Left Side */}
        <motion.button
          onClick={onClose}
          className="flex-1 max-w-[140px] h-12 px-6 rounded-xl font-medium text-sm
                     bg-white dark:bg-gray-800 
                     border border-gray-200 dark:border-gray-700
                     text-gray-700 dark:text-gray-300
                     hover:bg-gray-50 dark:hover:bg-gray-700
                     hover:border-gray-300 dark:hover:border-gray-600
                     transition-all duration-200
                     shadow-sm hover:shadow-md"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          Close
        </motion.button>

        {/* Primary Action Button - Right Side */}
        <motion.button
          onClick={onActionClick}
          className="flex-1 max-w-[200px] h-12 px-6 rounded-xl font-medium text-sm
                     bg-gradient-to-r from-purple-500 to-blue-500 
                     hover:from-purple-600 hover:to-blue-600
                     text-white shadow-lg hover:shadow-xl
                     transition-all duration-200
                     flex items-center justify-center gap-2"
          whileHover={{ 
            scale: 1.02, 
            y: -2,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <span>{actionButtonText}</span>
          <motion.div
            animate={{ x: [0, 4, 0] }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <ArrowRight className="h-4 w-4" />
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 2) * 40}%`,
            }}
            animate={{
              y: [-10, -20, -10],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LoadingSuccess;
